/**
 * Primer día de un usuario (o «Cambiar de libro»): crear su libro en su Google Drive, con sus cuentas y unas
 * categorías de partida, o elegir uno que ya tenga. Devuelve el id del libro guardado en su cuenta, o null si vuelve atrás.
 */
import { ACCOUNT_TYPES, STARTER_CATEGORIES, checkNewAccounts, newWorkbookPlan, type NewAccount } from '@pf/workbook';
import { api } from './adapters/api-client.js';
import { pickSpreadsheet } from './adapters/picker.js';
import { createWorkbook } from './adapters/workbook-create.js';
import { parseAmount } from './edit-views.js';
import { $, esc, todayIso } from './ui/format.js';
import { setView } from './views.js';

const BANKS = ['BBVA', 'ING', 'Laboral Kutxa', 'MyInvestor'];
interface Row { name: string; bank: string; type: string; shared: boolean; opening: string }

export function chooseOrCreateWorkbook(o: { email: string; name: string; canCancel: boolean }): Promise<string | null> {
  return new Promise(resolve => {
    $('#title').textContent = 'Tu libro';
    const lastYearEnd = `${Number(todayIso().slice(0, 4)) - 1}-12-31`;
    let rows: Row[] = [{ name: '', bank: '', type: 'Corriente', shared: false, opening: '' }];
    let title = 'Finanzas';
    let start = lastYearEnd;

    const welcome = (error = '') => {
      setView(`<section class="card"><h2>${o.name ? `Hola, ${esc(o.name)}` : 'Tu libro de finanzas'}</h2>
        <div class="sub">Todo lo que apuntes se guarda en <b>tu libro</b>: una hoja de Google Sheets en tu propio Google Drive. Solo tú la ves; la app solo tiene acceso a ese fichero.</div>
        ${error ? `<div class="error" style="margin-top:10px">${esc(error)}</div>` : ''}
        <div class="actions"><button class="btn" id="goCreate">Crear mi libro</button><button class="btn secondary" id="goPick">Ya tengo un libro: elegirlo</button></div>
        <div class="sub hint">Sesión iniciada como ${esc(o.email)}.${o.canCancel ? ' <a href="#/mas" id="cancel">Volver sin cambiar nada</a>' : ''}</div></section>`);
      $('#goCreate').onclick = () => form();
      $('#goPick').onclick = pick;
      const c = document.getElementById('cancel');
      if (c) c.onclick = e => { e.preventDefault(); resolve(null); };
    };

    const pick = async () => {
      try {
        const [cfg, token] = await Promise.all([api.config(), api.accessToken()]);
        const picked = await pickSpreadsheet(cfg, token, 'Elige tu libro de finanzas');
        if (!picked) return;
        resolve((await api.setWorkbook('finance', picked.id)).fileId);
      } catch (err) { welcome((err as Error).message); }
    };

    const rowHtml = (r: Row, i: number) => `<div class="onb-acc" data-i="${i}">
      <div class="split">
        <div class="field"><label>Nombre</label><input data-f="name" value="${esc(r.name)}" maxlength="40" placeholder="${i === 0 ? 'Nómina' : 'Ahorro'}"></div>
        <div class="field"><label>Banco</label><input data-f="bank" list="onbBanks" value="${esc(r.bank)}" maxlength="40" placeholder="BBVA"></div>
      </div>
      <div class="split">
        <div class="field"><label>Tipo</label><select data-f="type">${ACCOUNT_TYPES.map(t => `<option${t === r.type ? ' selected' : ''}>${t}</option>`).join('')}</select></div>
        <div class="field"><label>Saldo el ${esc(start.split('-').reverse().join('/'))}</label><input data-f="opening" inputmode="decimal" value="${esc(r.opening)}" placeholder="0,00"></div>
      </div>
      <label class="check"><input type="checkbox" data-f="shared"${r.shared ? ' checked' : ''}> Es una cuenta conjunta (solo cuenta tu mitad)</label>
      ${rows.length > 1 ? `<button class="linklike" data-del="${i}">Quitar esta cuenta</button>` : ''}
    </div>`;

    const read = () => {
      title = (document.getElementById('onbTitle') as HTMLInputElement | null)?.value ?? title;
      start = (document.getElementById('onbStart') as HTMLInputElement | null)?.value || start;
      document.querySelectorAll<HTMLElement>('.onb-acc').forEach(box => {
        const r = rows[Number(box.dataset.i)]!;
        const get = (f: string) => box.querySelector<HTMLInputElement>(`[data-f="${f}"]`)!;
        r.name = get('name').value; r.bank = get('bank').value; r.type = get('type').value; r.opening = get('opening').value; r.shared = get('shared').checked;
      });
    };

    const accountsOf = (): NewAccount[] => rows.map(r => ({ name: r.name.trim(), bank: r.bank.trim(), type: r.type,
      share: r.shared ? 0.5 : 1, inNetWorth: r.type !== 'Efectivo', opening: r.opening.trim() ? parseAmount(r.opening) : 0, openingDate: start }));

    const form = (error = '') => {
      setView(`<section class="card"><h2>Crear mi libro</h2>
        <div class="sub">Dime qué cuentas tienes. Lo demás (categorías, presupuesto…) lo irás ajustando desde la app.</div>
        ${error ? `<div class="error" style="margin-top:10px">${esc(error)}</div>` : ''}
        <div class="split" style="margin-top:12px">
          <div class="field"><label for="onbTitle">Nombre del libro</label><input id="onbTitle" value="${esc(title)}" maxlength="60"></div>
          <div class="field"><label for="onbStart">Empiezas a apuntar desde</label><input id="onbStart" type="date" value="${esc(start)}" max="${todayIso()}"></div>
        </div>
        <div class="sub hint">Lo normal es el 31 de diciembre pasado: luego importas los extractos del banco de este año. El saldo de cada cuenta es el de ese día (si no lo sabes, déjalo en blanco y lo pones después en la cuenta).</div>
        <h3 class="liq-sub">Tus cuentas</h3>
        <datalist id="onbBanks">${BANKS.map(b => `<option value="${b}">`).join('')}</datalist>
        <div id="onbRows">${rows.map(rowHtml).join('')}</div>
        <div class="actions"><button class="btn small secondary" id="onbAdd">＋ Otra cuenta</button></div>
        <div class="sub hint">Empezarás con unas categorías habituales (${STARTER_CATEGORIES.slice(8, 12).map(c => c[0]).join(', ')}…) que podrás cambiar.</div>
        <div class="actions"><button class="btn" id="onbCreate">Crear mi libro</button><button class="btn secondary" id="onbBack">Atrás</button></div>
      </section>`);
      $('#onbAdd').onclick = () => { read(); rows.push({ name: '', bank: '', type: 'Corriente', shared: false, opening: '' }); form(); };
      $('#onbBack').onclick = () => { read(); welcome(); };
      $('#onbStart').onchange = () => { read(); form(); };
      $('#onbRows').onclick = e => {
        const d = (e.target as HTMLElement).dataset.del;
        if (d !== undefined) { read(); rows.splice(Number(d), 1); form(); }
      };
      $('#onbCreate').onclick = async () => {
        read();
        const accounts = accountsOf();
        const problem = !title.trim() ? 'Ponle nombre al libro' : accounts.some(a => Number.isNaN(a.opening)) ? 'Revisa los saldos: escribe solo el número (p. ej. 1.250,50)' : checkNewAccounts(accounts);
        if (problem) return form(problem);
        setView('<section class="card"><h2>Creando tu libro…</h2><div class="sub">Se está creando en tu Google Drive. Tarda unos segundos.</div><div class="loading"></div></section>');
        try {
          const plan = newWorkbookPlan({ title: title.trim(), today: todayIso(), accounts });
          const { id } = await createWorkbook(plan, () => api.accessToken());
          resolve((await api.setWorkbook('finance', id)).fileId);
        } catch (err) { form(`No se pudo crear: ${(err as Error).message}`); }
      };
    };

    welcome();
  });
}
